import React, { useState, cloneElement, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { useToast } from "@/hooks/use-toast";
import { useWindowBeforeUnload } from "@/hooks/use-window-beforeunload";
import { AUDIO_VALIDATE_ERROR_TYPE, validateAudio } from "@/utils/audio-validator";
import { customAudioHooks } from "@/manager";
import { X } from "lucide-react";
import { UploadStep } from "./upload-step";
import { SaveStep } from "./save-step";
import {
  AUDIO_MAX_DURATION,
  AUDIO_MIN_DURATION,
} from "./constants";
import { cn } from "@/lib/utils";
import { UPLOAD_ERROR_TYPE, UploadErrorReason } from "@/hooks/use-upload";

type Step = 0 | 1;

interface CreateAudioModalProps {
  // trigger 模式
  trigger?: React.ReactElement;
  // open 参数模式
  open?: boolean;
  name?: string;
  file?: File | null;
  userId?: string;
  // 视频URL 可选 当文件是视频的时候可选择提供
  videoURL?: string;
  uploadType?: "audio" | "video";
  /** 生成音频类型，默认 ElevenLabs */
  generateType?: "ElevenLabs" | "Minimax";
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (audio: Audio) => void;
}

export const CreateAudioModal: React.FC<CreateAudioModalProps> = ({
  trigger,
  open: externalOpen,
  name: externalName,
  file: externalFile,
  videoURL,
  uploadType = "audio",
  generateType = "ElevenLabs",
  onOpenChange,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    file,
    name,
    message,
    isAllowStep,
    isAgentUserManagementDetail,
    setAudioName,
    setAudioFile,
    init,
  } = customAudioHooks.useCustomAudio();
  const { progress, clone } = customAudioHooks.useCustomAudioClone();
  const { reset } = customAudioHooks.useCustomAudioReset();
  const { save } = customAudioHooks.useCustomAudioSave();
  const [internalOpen, setInternalOpen] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const hasInitializedOnOpenRef = useRef(false);
  // 最终合成的音频URL
  const [URL, setURL] = useState("");
  const combinedProgress = isGenerating ? Math.min(progress, 99) : progress;
  // 控制弹窗开关
  const isOpen = externalOpen !== undefined ? externalOpen : internalOpen;
  const handleOpenChange = onOpenChange || setInternalOpen;

  // 处理音频命名
  const handleChange = (name: string) => {
    setAudioName(name);
  };

  // 移除音频
  const handleRemove = () => {
    setAudioFile(null);
  };

  // 处理上传
  const handleUpload = async (file: File) => {
    // 校验音频大小和时长
    const validateError = await validateAudio(file, {
      minDuration: AUDIO_MIN_DURATION,
      maxDuration: AUDIO_MAX_DURATION,
    });
    if (validateError) {
      let description: string;
      switch (validateError) {
        case AUDIO_VALIDATE_ERROR_TYPE.INVALID_MIN_DURATION:
          description = t("custom_digital_human.upload_form.upload_audio_min_duration");
          break;
        case AUDIO_VALIDATE_ERROR_TYPE.INVALID_MAX_DURATION:
          description = t("custom_digital_human.upload_form.upload_audio_max_duration");
          break;
        case AUDIO_VALIDATE_ERROR_TYPE.INVALID_MAX_FILE_SIZE:
          description = t("custom_digital_human.create_form.upload_audio_max_filesize");
          break;
        default:
          description = t("custom_digital_human.create_form.invalid_audio_description");
      }
      toast.default({
        title: t("custom_digital_human.create_form.invalid_audio"),
        description,
      });
      return;
    }
    setStep(0);
    setAudioFile(file);
  };

  // 处理无效文件
  const handleInvalid = (reason?: UploadErrorReason) => {
    let description: string;
    switch (reason) {
      case UPLOAD_ERROR_TYPE.INVALID_MAX_SIZE:
        description = t("custom_digital_human.upload_form.upload_audio_max_filesize");
        break;
      default:
        description = t("custom_digital_human.create_form.invalid_audio_description");
    }
    toast.default({
      title: t("custom_digital_human.create_form.invalid_audio"),
      description,
    });
  };

  /**
   * 处理生成音频
   * 1、视频生数字人 -> 克隆音频接口
   * 2、普通创建音频 -> 上传&生成音频接口
   */
  const handleSubmit = async ({ spendCredits }: { spendCredits: number }) => {
    setStep(1);
    setIsGenerating(true);
    try {
      await clone(generateType, spendCredits);
      setURL(window.URL.createObjectURL(file));
      await handleSave();
      reset();
      handleOpenChange(false);
      setStep(0);
    } catch (error) {
      setStep(0);
    } finally {
      setIsGenerating(false);
    }
  };

  // 处理保存
  const handleSave = async () => {
    try {
      const audioInfo = await save();
      // 将保存成功的音频信息传递给外部更新数据
      onSubmit?.({
        id: audioInfo.id,
        voiceTtsId: audioInfo.voiceTtsId,
        modelName: audioInfo.provider,
        name: audioInfo.name,
        ageGroup: "",
        language: "",
        gender: "",
        speed: 1,
        audioUrl: audioInfo.sampleUrl,
        isStarred: false,
        createdAt: audioInfo.createdAt,
      });
    } catch (error) {
      setStep(0);
      throw error;
    }
  };

  // 重置状态
  const handleClose = () => {
    if (isGenerating) return;
    setStep(0);
    reset();
    handleOpenChange(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open && isGenerating) return;
    handleOpenChange(open);
  };

  // trigger 点击处理
  const handleTriggerClick = () => {
    handleOpenChange(true);
  };

  useEffect(() => {
    if (!isOpen) {
      hasInitializedOnOpenRef.current = false;
      return;
    }

    // 每次打开仅初始化一次，避免重渲染时反复 init 覆盖输入并触发重复校验请求
    if (hasInitializedOnOpenRef.current) return;
    hasInitializedOnOpenRef.current = true;

    if (externalOpen) {
      init({ uploadType, name: externalName, file: externalFile, url: videoURL });
    }
  }, [isOpen, externalOpen, externalName, externalFile, uploadType, videoURL, init]);

  // 页面拦截保护
  useWindowBeforeUnload({ enabled: isGenerating });

  const modalContent = (
    <Dialog.Portal>
      <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
      <Dialog.Content className="flex flex-col fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-50 w-[40rem] max-w-[90vw] max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5 px-6 py-4 border-b">
          <Dialog.Title className="text-i-normal text-lg font-medium">
            {uploadType === "audio" ? t("custom_digital_human.create_form.create_voice") : t("custom_digital_human.create_form.clone_voice")}
          </Dialog.Title>
          <Dialog.Close asChild>
            <button
              className={cn(
                "w-10 h-10 flex justify-center items-center hover:bg-gray-100 border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_rgba(16,24,40,0.05)]",
                isGenerating && "opacity-50 cursor-not-allowed hover:bg-transparent"
              )}
              tabIndex={-1}
              disabled={isGenerating}
              onClick={handleClose}
            >
              <X className="w-4 h-4" />
            </button>
          </Dialog.Close>
        </div>

        {/* 内容区域 */}
        {step === 0 && (
          <UploadStep
            file={file}
            name={name}
            isAllowStep={isAllowStep}
            uploadType={uploadType}
            progress={100}
            onChange={handleChange}
            onUpload={handleUpload}
            onInvalid={handleInvalid}
            onRemove={handleRemove}
            onSubmit={handleSubmit}
            onClose={handleClose}
            errorMessage={message}
          />
        )}
        {step === 1 && (
          <SaveStep
            progress={combinedProgress}
            type={file?.type?.startsWith("audio") ? "create" : "clone"}
            name={name}
            URL={URL}
            onUpload={handleUpload}
            onInvalid={handleInvalid}
            onSave={handleSave}
            onCancel={handleClose}
          />
        )}
      </Dialog.Content>
    </Dialog.Portal>
  );

  // trigger 模式
  if (trigger) {
    return (
      <Dialog.Root open={isOpen} onOpenChange={handleDialogOpenChange}>
        <Dialog.Trigger asChild>
          {cloneElement(trigger, {
            onClick: (e: React.MouseEvent) => {
              trigger.props.onClick?.(e);
              handleTriggerClick();
            },
          })}
        </Dialog.Trigger>
        {modalContent}
      </Dialog.Root>
    );
  }

  // open 参数模式
  return (
    <Dialog.Root open={isOpen} onOpenChange={handleDialogOpenChange}>
      {modalContent}
    </Dialog.Root>
  );
};
